import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Collapse, Empty, Flex, Input, Skeleton, Typography } from 'antd';
import { MutableRefObject, useContext, useLayoutEffect, useRef } from 'react';
import VSpace from '../../../../common/VSpace';
import ReadyToInvoiceContext from '../../ReadyToInvoiceContext';
import AdditionalFilterFields from '../../../InvoiceByJob/AdditionalFilterFields'
import DateRangeFilter from '../../../InvoiceByJob/DateRangeFilter'
import { jobCountLabel } from '../editorDrawerModel';
import JobCard from './JobCard';
import { DATE_HELP } from '../../readyToInvoiceModel'
const { Text } = Typography

export type JobsMenuScrollState = { top: number; initialScrollPending: boolean }

type Props = {
  filtersExpanded: boolean
  onFiltersExpandedChange: (expanded: boolean) => void
  isVisible: boolean
  scrollState: MutableRefObject<JobsMenuScrollState>
}

const JobsSection = ({ isVisible, scrollState, filtersExpanded, onFiltersExpandedChange }: Props) => {
  const { currentSelection, selectJob, jobs, isLoadingJobs, filters, hasJobsError, refreshJobs } = useContext(ReadyToInvoiceContext)

  const scrollContainer = useRef<HTMLDivElement>(null)
  const selectedCard = useRef<HTMLDivElement>(null)
  const selectedJobId = currentSelection?.jobId
  const hasSelectedJob = jobs.some(job => job.id === selectedJobId)

  useLayoutEffect(() => {
    const container = scrollContainer.current
    const card = selectedCard.current
    if (!isVisible || isLoadingJobs || !container) {
      return
    }
    // The editor/catalog remount for each job; keep the menu at its previous position.
    if (!scrollState.current.initialScrollPending) {
      container.scrollTop = scrollState.current.top
      return
    }
    if (!card) {
      return
    }
    const viewport = container.getBoundingClientRect()
    const bounds = card.getBoundingClientRect()
    container.scrollTop += bounds.top - viewport.top
    scrollState.current = { top: container.scrollTop, initialScrollPending: false }
  }, [isVisible, selectedJobId, hasSelectedJob, isLoadingJobs, scrollState])

  return <Flex vertical style={{ height: 'calc(100vh - 130px)', minHeight: 0, overflow: 'hidden', backgroundColor: 'white' }}>
    <Flex vertical style={{ padding: 16, flexShrink: 0, backgroundColor: '#f5f5f5' }} gap={16}>
      <Input
        placeholder="Search by customer or job name"
        suffix={<FontAwesomeIcon icon={faSearch} />}
        value={filters.state.search}
        onChange={(e) => filters.setSearch(e.target.value)}
      />

      <Collapse activeKey={filtersExpanded ? ['filters'] : []}
        onChange={keys => onFiltersExpandedChange(keys.length > 0)} style={{ backgroundColor: 'white' }}
        items={[
          {
            key: 'filters',
            label: 'Filters',
            children: <VSpace size="large">
              <div>
                <div style={{ marginBottom: 4 }}>Date Range</div>
                <DateRangeFilter filters={filters} dateHelp={DATE_HELP} style={{ width: '100%' }} />
              </div>
              <AdditionalFilterFields filters={filters} showVisitReviewStatus />
            </VSpace>
          },
        ]}
      />
      <Text type='secondary'>{jobCountLabel(jobs.length)}</Text>
    </Flex>

    <div ref={scrollContainer} role="region" aria-label="Jobs" onScroll={event => {
      if (!scrollState.current.initialScrollPending) {
        scrollState.current.top = event.currentTarget.scrollTop
      }
    }} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {hasJobsError && <Alert type="error" message="Unable to load jobs." action={<Button onClick={refreshJobs}>Retry</Button>} />}
      {!isLoadingJobs && !hasJobsError && jobs.length === 0 && <Empty description="No jobs match these filters" />}
      {isLoadingJobs
        ? <Skeleton active paragraph={{ rows: 5 }} style={{ padding: 10 }} />
        : <VSpace style={{ padding: 10 }}>
          {jobs.map(job => <div key={job.id} ref={selectedJobId === job.id ? selectedCard : undefined}>
            <JobCard
              job={job}
              isSelected={selectedJobId === job.id}
              onSelectJob={selectJob}
            />
          </div>)}
        </VSpace>}
    </div>
  </Flex>
};

export default JobsSection;
